import { RateOwnerType } from '@treadinc/horizon-api-spec';
import { RateType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { forwardRef, useImperativeHandle, useRef } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { FormStateChangeProps } from '~formsShared/types';
import { Account } from '~hooks/useAccount/models';
import { Rate } from '~hooks/useRates/models';
import { User } from '~hooks/useUsers/models';
import { RatesFormSchemaInterface } from '~pages/Settings/RatesManagement/rateFormValidationSchema';
import { Nullable } from '~types/Nullable';

import { RateForm, RateFormRef } from './RateForm';

interface CreateEditRateCardDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRate: Nullable<Rate>;
  selectedRateId: string | null;
  isUpdatingRate: boolean;
  isDirty: boolean;
  onFormStateChange: (props: FormStateChangeProps) => void;
  onSubmit: () => void;
  rateFormDialogTitle: string;
  defaultRateOwnerType?: RateOwnerType;
  selectedAccount: Nullable<Account>;
  selectedDriver: Nullable<User>;
}

export interface CreateEditRateCardDialogRef {
  submit: (callback: (data: RatesFormSchemaInterface) => void) => void;
}

export const CreateEditRateCardDialog = forwardRef<
  CreateEditRateCardDialogRef,
  CreateEditRateCardDialogProps
>(
  (
    {
      isOpen,
      onClose,
      selectedRate,
      selectedRateId,
      isUpdatingRate,
      onFormStateChange,
      onSubmit,
      rateFormDialogTitle,
      defaultRateOwnerType,
      selectedAccount,
      selectedDriver,
    },
    ref,
  ) => {
    const rateDialogRef = useRef<RateFormRef>(null);

    useImperativeHandle(ref, () => ({
      submit: (callback: (data: RatesFormSchemaInterface) => void) => {
        rateDialogRef.current?.submit(callback);
      },
    }));

    return (
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        maxWidth="md"
        preventCloseOnBackdropClick
        preventCloseOnEscapeKeyDown
      >
        <DialogHeader onClose={onClose}>
          {`${selectedRateId?.length ? t('administration.rates.update_rate') : t('administration.rates.create_rate')} - ${rateFormDialogTitle}`}
        </DialogHeader>

        <DialogContent>
          <RateForm
            subFormsToHide={
              defaultRateOwnerType === RateOwnerType.CUSTOMER
                ? [RateType.RATE_COMMISSION, RateType.RATE_FLAT_COMMISSION]
                : []
            }
            account={selectedAccount}
            ref={rateDialogRef}
            defaultRate={(selectedRate as unknown as Rate) || {}}
            onFormStateChange={onFormStateChange}
            driver={selectedDriver}
            defaultRateOwnerType={defaultRateOwnerType}
          />
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={isUpdatingRate}
            onClick={onClose}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton loading={isUpdatingRate} onClick={onSubmit}>
            {selectedRateId ? t('actions.update') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
    );
  },
);

CreateEditRateCardDialog.displayName = 'CreateEditRateCardDialog';
